import React, { useEffect, useState } from 'react'
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Drawer,
  IconButton,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
  useTheme,
} from '@mui/material'
import { AdminOrganizationUpdate, AuthProvider, OrganizationFull, OrgStatus, Product, User } from 'api/model'
import { useRecoilValue, useSetRecoilState } from 'recoil'
import ReadOnlyKeyPair from 'components/ReadOnlyKeyPair'
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined'
import { adminOrgsAtom, authenticatedUserAtom, toastAtom } from 'state'
import { formatTimestamp, getColorForDarkMode, isFieldEmpty, isWebAddressValid } from 'utils'
import CityAutocomplete from 'components/Organization/CityAutocomplete'
import IndustryPicker from 'components/Organization/IndustryPicker'
import { DatePicker } from '@mui/x-date-pickers'
import OrganizationLogo from 'components/OrganizationLogo'
import ModeEditOutlinedIcon from '@mui/icons-material/ModeEditOutlined'
import {
  adminDeleteOrgPicture,
  adminDeleteOrgUser,
  adminUpdateOrg,
  adminUploadOrgPicture,
} from 'api/endpoints/jelledFromFileSpecWithTransformer'
import EditablePicture from 'components/EditablePicture'
import { useFetchProducts } from 'hooks/use-admin'
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined'
import { useActiveOrganization, useFetchMembersForOrganization } from 'hooks/use-organization'
import useCheckMobileScreen from 'hooks/use-check-mobile-screen'
import { TextFieldWithDarkMode } from 'dark-mode-components'
import Selector from 'components/Selector'
import { COLORS } from 'app-constants'
import JelledSwitch from 'components/JelledSwitch'

const UserRow = ({
  user,
  organizationId,
  setMembersForOrg,
  isDeletionEnabled,
  isDeletionRequested,
}: {
  user: User
  organizationId: string
  setMembersForOrg: React.Dispatch<React.SetStateAction<User[]>>
  isDeletionEnabled: boolean
  isDeletionRequested: boolean
}) => {
  const [activeOrganization] = useActiveOrganization()
  const theme = useTheme()
  const [isHovered, setIsHovered] = useState(false)
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)
  const setToast = useSetRecoilState(toastAtom)

  const [isDeletingUser, setIsDeletingUser] = useState(false)
  const [deleteSubmitCount, setDeleteSubmitCount] = useState(0)

  // delete the user
  const onDeleteUser = () => {
    setIsDeleteDialogOpen(true)
  }

  const onDeleteConfirmed = async () => {
    if (!organizationId || !activeOrganization) {
      return
    }

    setIsDeletingUser(true)
    setDeleteSubmitCount((count) => count + 1)
    try {
      await adminDeleteOrgUser(
        activeOrganization,
        {
          reason: deletionReason,
        },
        {
          delete_org_id: organizationId,
          delete_user_id: user.id,
        }
      )
      setMembersForOrg((members) => {
        return members.filter((m) => m.id !== user.id)
      })
      setToast({ title: 'Successfully removed the user', severity: 'success' })
    } catch (err) {
      setToast({ title: 'Failed to remove the user', severity: 'error' })
    } finally {
      setIsDeletingUser(false)
    }
  }

  const [deletionReason, setDeletionReason] = useState<string>('')
  const onDeletionReasonChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setDeletionReason(event.target.value)
    setDeleteSubmitCount(0)
  }

  const renderConfirmDeleteDialog = () => {
    return (
      <Dialog open={isDeleteDialogOpen}>
        <DialogTitle>Are you sure you want to delete {user?.name ?? user?.email}?</DialogTitle>
        <DialogContent>
          <Stack mt={3}>
            <TextField
              className="has-label"
              label="Deletion reason"
              placeholder="Enter a reason"
              value={deletionReason}
              onChange={onDeletionReasonChange}
              disabled={isDeletingUser}
              error={deleteSubmitCount > 0 && isFieldEmpty(deletionReason)}
              helperText={deleteSubmitCount > 0 && isFieldEmpty(deletionReason) && 'You must enter deletion reason'}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setIsDeleteDialogOpen(false)
            }}
            disabled={isDeletingUser}
            sx={{
              color: getColorForDarkMode(theme),
            }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={onDeleteConfirmed}
            disabled={isDeletingUser || !deletionReason}>
            Yes, delete
          </Button>
        </DialogActions>
      </Dialog>
    )
  }

  return (
    <Stack
      height="48px"
      direction="row"
      alignItems="center"
      py={1}
      pl={2}
      pr={2}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      sx={{
        borderRadius: '8px',
        background: isHovered ? COLORS.GRAY_8 : 'transparent',
      }}>
      <Typography
        variant="h6"
        sx={{
          flex: '0 0 148px',
          mr: 2,
        }}>
        {user.name}
      </Typography>

      <Typography
        variant="h6"
        sx={{
          flex: '0 0 188px',
          mr: 2,
        }}>
        {user.email}
      </Typography>

      <Typography
        variant="h6"
        sx={{
          width: '100%',
        }}>
        {isDeletionRequested ? 'Deletion requested' : user.status}
      </Typography>

      {isHovered && isDeletionEnabled && (
        <IconButton onClick={onDeleteUser}>
          <DeleteOutlinedIcon />
        </IconButton>
      )}
      {renderConfirmDeleteDialog()}
    </Stack>
  )
}

const UsersTab = ({
  membersForOrg,
  setMembersForOrg,
  organizationId,
}: {
  membersForOrg: User[]
  setMembersForOrg: React.Dispatch<React.SetStateAction<User[]>>
  organizationId: string
}) => {
  const user = useRecoilValue(authenticatedUserAtom)

  return (
    <Stack px={4}>
      <Stack direction="row" alignItems="center" pl={2} pr={4} mb={2} py={1}>
        <Typography
          variant="h6"
          sx={{
            flex: '0 0 148px',
            mr: 2,
          }}>
          Name
        </Typography>

        <Typography
          variant="h6"
          sx={{
            flex: '0 0 188px',
            mr: 2,
          }}>
          Email
        </Typography>

        <Typography
          variant="h6"
          sx={{
            width: '100%',
          }}>
          Status
        </Typography>
      </Stack>

      {membersForOrg.map((member) => {
        // the deletion may not be enabled
        const isDeletionEnabled = member.id !== user?.id && membersForOrg.length > 1
        return (
          <UserRow
            isDeletionEnabled={isDeletionEnabled}
            isDeletionRequested={member.deletion_requested}
            organizationId={organizationId}
            user={member}
            key={member.id}
            setMembersForOrg={setMembersForOrg}
          />
        )
      })}
    </Stack>
  )
}

const AdminOrganizationSidebar = ({
  organizationId,
  open,
  onDeleteClicked,
  onClose,
}: {
  organizationId: string | null
  open: boolean
  onDeleteClicked: (org: OrganizationFull) => void
  onClose: () => void
}) => {
  const theme = useTheme()
  const isMobile = useCheckMobileScreen()

  const adminOrgs = useRecoilValue(adminOrgsAtom)
  const organization = adminOrgs.find((o) => o.id === organizationId)

  const { membersForOrg, setMembersForOrg } = useFetchMembersForOrganization(organizationId ?? '')

  const [isEditing, setIsEditing] = useState(false)
  const onEditClicked = () => {
    setIsEditing(!isEditing)
  }

  // we have two tabs, account, and users
  const [tabValue, setTabValue] = useState(0)
  const onTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue)
  }

  useEffect(() => {
    setIsEditing(false)
  }, [open])

  if (isMobile) {
    return (
      <Stack>
        <Box width="100%" id="AdminOrganizationSidebar-container" pb={2}>
          <Box px={2}>
            {organization && !isEditing && (
              <ReadOrganization
                organization={organization}
                onEditClicked={onEditClicked}
                onDeleteClicked={onDeleteClicked}
              />
            )}
            {organization && isEditing && (
              <EditOrganization
                organization={organization}
                onFinishedEditing={() => {
                  setIsEditing(false)
                }}
              />
            )}
          </Box>
        </Box>
      </Stack>
    )
  }

  const renderTabContent = () => {
    switch (tabValue) {
      case 0:
        return (
          <Box px={4}>
            {organization && !isEditing && (
              <ReadOrganization
                organization={organization}
                onEditClicked={onEditClicked}
                onDeleteClicked={onDeleteClicked}
              />
            )}
            {organization && isEditing && (
              <EditOrganization
                organization={organization}
                onFinishedEditing={() => {
                  setIsEditing(false)
                }}
              />
            )}
          </Box>
        )
      case 1:
        return (
          <UsersTab
            organizationId={organizationId ?? ''}
            membersForOrg={membersForOrg}
            setMembersForOrg={setMembersForOrg}
          />
        )
    }
  }

  return (
    <Drawer anchor="right" open={open} onClose={onClose}>
      <Box width={isMobile ? '100%' : 640} id="AdminOrganizationSidebar-container" pb={3}>
        <Stack direction="row" justifyContent="flex-end" pt={1} pr={1}>
          <IconButton
            onClick={onClose}
            sx={{
              path: {
                fill: getColorForDarkMode(theme),
              },
            }}>
            <CloseOutlinedIcon />
          </IconButton>
        </Stack>

        {organization && (
          <Stack direction="row" alignItems="center" spacing={2} mb={4} pl={4}>
            <OrganizationLogo organization={organization} style={{ width: '48px', height: '48px' }} />
            <Typography variant="h4" flex="1 1">
              {organization.name}
            </Typography>
          </Stack>
        )}

        <Tabs
          orientation="horizontal"
          value={tabValue}
          onChange={onTabChange}
          textColor="secondary"
          sx={{
            pl: 4,
            mb: 4,
            '.MuiTabs-flexContainer': {
              gap: isMobile ? 0 : 4,
            },
          }}>
          <Tab
            label="Account"
            sx={{
              fontWeight: 500,
              '&.Mui-selected': {
                color: 'primary.main',
                fontWeight: 600,
              },
            }}
          />
          <Tab
            label="Users"
            sx={{
              fontWeight: 500,
              '&.Mui-selected': {
                color: 'primary.main',
                fontWeight: 600,
              },
            }}
          />
        </Tabs>

        {renderTabContent()}
      </Box>
    </Drawer>
  )
}

const ReadOrganization = ({
  organization,
  onEditClicked,
  onDeleteClicked,
}: {
  organization: OrganizationFull
  onEditClicked: () => void
  onDeleteClicked: (org: OrganizationFull) => void
}) => {
  const [activeOrganization] = useActiveOrganization()

  return (
    <Stack position="relative" height="calc(100vh - 206px)">
      <Stack spacing={3} height="100%">
        <ReadOnlyKeyPair name="Company name" value={organization.name} />
        <ReadOnlyKeyPair name="Company domain" value={organization.email_domain} />
        <ReadOnlyKeyPair name="Status" value={organization.status} />
        <ReadOnlyKeyPair name="Expiration date" value={formatTimestamp(organization.subscription.date_expires)} />
        <ReadOnlyKeyPair
          name="Deletion status"
          value={organization.deletion_requested ? 'Deletion requested' : 'N/A'}
        />
        <ReadOnlyKeyPair name="Product" value={organization.subscription.product_name} />
        <ReadOnlyKeyPair
          name="Auth Provider"
          value={
            organization.auth_providers && organization.auth_providers.length > 0 ? organization.auth_providers[0] : ''
          }
        />
        <ReadOnlyKeyPair
          name="Process images in attachments"
          value={organization.use_image_conversion ? 'Enabled' : 'Disabled'}
        />
        <ReadOnlyKeyPair name="Description" value={organization.description} />
        <ReadOnlyKeyPair name="Company website" value={organization.website} href={organization.website} />
        <ReadOnlyKeyPair name="Location" value={organization.location} />
        <ReadOnlyKeyPair name="Industry" value={organization.industry} />
        {organization.founding_year && <ReadOnlyKeyPair name="Founding year" value={`${organization.founding_year}`} />}
      </Stack>
      <Stack justifyContent="space-between" alignItems="center" direction="row">
        <Button
          variant="outlined"
          color="secondary"
          startIcon={<DeleteOutlinedIcon />}
          onClick={() => {
            onDeleteClicked(organization)
          }}
          sx={{
            visibility: organization.id !== activeOrganization ? 'visible' : 'hidden',
          }}>
          Delete
        </Button>

        <Button color="primary" variant="contained" startIcon={<ModeEditOutlinedIcon />} onClick={onEditClicked}>
          Edit
        </Button>
      </Stack>
    </Stack>
  )
}

const orgStatusOptions: OrgStatus[] = [OrgStatus.active, OrgStatus.inactive]

const EditOrganization = ({
  organization,
  onFinishedEditing,
}: {
  organization: OrganizationFull
  onFinishedEditing: () => void
}) => {
  const [activeOrganization] = useActiveOrganization()
  const theme = useTheme()
  const isMobile = useCheckMobileScreen()

  const { products } = useFetchProducts()
  const authProviders: AuthProvider[] = Object.values(AuthProvider)

  const setToast = useSetRecoilState(toastAtom)
  const setAdminOrgs = useSetRecoilState(adminOrgsAtom)

  const [name, setName] = useState<string>(organization.name)
  const onNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setName(event.target.value)
  }

  const [status, setStatus] = useState<OrgStatus>(organization.status)

  const [product, setProduct] = useState<Product | null>(null)
  useEffect(() => {
    // if the organization has a product, set it as the default
    if (organization.subscription.product_name && products.length > 0) {
      // find the product in the list of products
      const product = products.find((p) => p.name === organization.subscription.product_name)
      if (product) {
        setProduct(product)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [products, organization.subscription.product_name])

  const [authProvider, setAuthProvider] = useState(
    organization.auth_providers && organization.auth_providers.length > 0 ? organization.auth_providers[0] : ''
  )

  const [modelType, setModelType] = useState<string>(organization.base_model)
  const [summaryModelType, setSummaryModelType] = useState<string>(organization.summary_model)
  const [emailIntentType, setEmailIntentType] = useState<string>(organization.email_intent_model ?? 'gpt-4o-mini')

  const [description, setDescription] = useState<string>(organization.description ?? '')
  const onDescriptionChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setDescription(event.target.value)
  }

  const [website, setWebsite] = useState<string>(organization.website ?? '')
  const onWebsiteChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setWebsite(event.target.value)
  }

  const [location, setLocation] = useState<string>(organization.location ?? '')
  const onLocationChange = (value: string | null) => {
    setLocation(value ?? '')
  }

  const [industry, setIndustry] = useState<string>('')
  useEffect(() => {
    if (!industry && organization?.industry) {
      setIndustry(organization?.industry)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organization.industry])

  const [year, setYear] = useState<Date | null>(() => {
    let currentDateYear: Date | null = null
    if (organization.founding_year) {
      currentDateYear = new Date()
      currentDateYear.setFullYear(organization.founding_year)
    }
    return currentDateYear
  })
  const onYearChange = (date: Date | null | undefined) => {
    if (!date) {
      setYear(null)
      return
    }

    setYear(date)
  }

  const [useImageConversion, setUseImageConversion] = useState<boolean>(false)
  const onChangeImageConversation = async (event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
    setUseImageConversion(checked)
  }
  useEffect(() => {
    if (organization.use_image_conversion) {
      setUseImageConversion(organization.use_image_conversion)
    }
  }, [organization.use_image_conversion])

  const [isSubmitting, setIsSubmitting] = useState(false)
  const onFormSubmit = async () => {
    if (!name || !activeOrganization) {
      return
    }

    try {
      setIsSubmitting(true)

      const update: AdminOrganizationUpdate = {
        base_model: modelType,
        summary_model: summaryModelType,
        name,
        status,
        product_id: !!product ? product.id : undefined,
        auth_provider: !!authProvider ? (authProvider as AuthProvider) : undefined,
        description,
        website,
        location,
        industry,
        founding_year: year?.getFullYear(),
        use_image_conversion: useImageConversion,
        email_intent_model: emailIntentType,
      }
      const result = await adminUpdateOrg(activeOrganization, update, {
        update_org_id: organization.id,
      })
      setAdminOrgs((orgs) => {
        return orgs.map((o) => {
          if (o.id === organization.id) {
            return result.data
          }

          return o
        })
      })
    } catch (error) {
      setToast({ title: 'Update failed', severity: 'error' })
    } finally {
      setIsSubmitting(false)
      onFinishedEditing()
    }
  }

  const onPicturePicked: React.ChangeEventHandler<HTMLInputElement> = async (event) => {
    if (!organization) {
      return
    }

    if (!event.target.files) {
      return
    }

    const file = event.target.files[0]

    try {
      const result = await adminUploadOrgPicture(organization.id, file, {
        headers: { 'Content-Type': 'application/octet-stream' },
      })

      setAdminOrgs((orgs) => {
        return orgs.map((o) => {
          if (o.id === organization.id) {
            return {
              ...organization,
              picture: result.data.picture,
            }
          }

          return o
        })
      })
    } catch (Error) {
      setToast({ title: 'Failed to upload logo', severity: 'error' })
    }
  }

  const onPictureRemoved = async () => {
    if (!organization) {
      return
    }

    try {
      await adminDeleteOrgPicture(organization.id)
      setAdminOrgs((orgs) => {
        return orgs.map((o) => {
          if (o.id === organization.id) {
            const { picture, ...updatedOrganization } = o
            return updatedOrganization
          }

          return o
        })
      })
    } catch (error) {
      setToast({ title: 'Logo removal failed', severity: 'error' })
    }
  }

  return (
    <>
      <Stack
        spacing={4}
        justifyContent="flex-start"
        width="100%"
        style={{
          overflowY: 'scroll',
          maxHeight: isMobile ? '80vh' : 'auto',
        }}>
        <Typography variant="h4">Edit account</Typography>

        <Box width="160px">
          <EditablePicture
            isDisabled={false}
            onPicturePicked={onPicturePicked}
            onPictureRemoved={onPictureRemoved}
            src={organization.picture}
            changeLabel="logo"
            alt="Company profile"
          />
        </Box>

        <TextFieldWithDarkMode
          className="has-label"
          isDarkBackdrop={true}
          theme={theme}
          label="Company name"
          placeholder="Enter name"
          value={name}
          onChange={onNameChange}
          disabled={isSubmitting}
          error={isFieldEmpty(name)}
          helperText={isFieldEmpty(name) && 'You must enter a company name'}
        />

        <Selector
          id="status-selector"
          option={status}
          label="Status"
          setOption={setStatus}
          options={orgStatusOptions}
          isDarkBackdrop={false}
          width="240px"
        />

        {products.length > 0 && (
          <Selector
            id="product-selector"
            option={product}
            label="Product"
            setOption={setProduct}
            options={products}
            isDarkBackdrop={false}
            width="240px"
            renderCustomOption={(option: any) => (
              <Stack>
                <Typography
                  variant="pS"
                  sx={{
                    color: COLORS.GRAY_0,
                  }}>
                  {option.name}
                </Typography>
                <Typography
                  variant="pXS"
                  sx={{
                    color: COLORS.GRAY_0,
                  }}>
                  Expires on: {formatTimestamp(option.expires_on)}
                </Typography>
              </Stack>
            )}
          />
        )}

        <Selector
          label="Auth provider"
          id="auth-provider-selector"
          option={authProvider}
          setOption={setAuthProvider}
          options={authProviders}
          isDarkBackdrop={false}
          width="240px"
        />

        <Selector
          label="Model type"
          id="model-type-selector"
          option={modelType}
          setOption={setModelType}
          options={organization.available_base_models}
          isDarkBackdrop={false}
          width="240px"
        />

        <Selector
          label="Summary model type"
          id="summary-model-type-selector"
          option={summaryModelType}
          setOption={setSummaryModelType}
          options={organization.available_summary_models}
          isDarkBackdrop={false}
          width="240px"
        />

        <Selector
          label="Email intent type"
          id="email-intent-type-selector"
          option={emailIntentType}
          setOption={setEmailIntentType}
          options={organization.available_email_intent_models ?? []} // TODO: confirm whether it should be optional
          isDarkBackdrop={false}
          width="240px"
        />

        <JelledSwitch
          label={`Process images in attachments`}
          checked={useImageConversion}
          onChange={onChangeImageConversation}
          style={{
            color: COLORS.GRAY_0,
            marginTop: '12px',
            width: isMobile ? '326px' : '500px',
            marginBottom: '12px',
          }}
        />

        <TextFieldWithDarkMode
          className="has-label"
          isDarkBackdrop={true}
          theme={theme}
          multiline
          rows={3}
          label="Description"
          placeholder="Add here"
          value={description}
          onChange={onDescriptionChange}
          disabled={isSubmitting}
        />

        <TextFieldWithDarkMode
          className="has-label"
          isDarkBackdrop={true}
          theme={theme}
          label="Company website"
          placeholder="mycompany.com"
          value={website}
          onChange={onWebsiteChange}
          disabled={isSubmitting}
          error={!isWebAddressValid(website, true)}
          helperText={!isWebAddressValid(website, true) && 'You must enter a valid website'}
        />

        <CityAutocomplete initialValue={location} onValueChanged={onLocationChange} disabled={isSubmitting} />

        <IndustryPicker value={industry} setIndustry={setIndustry} disabled={isSubmitting} />

        <DatePicker
          views={['year']}
          label="Founding year"
          value={year}
          onChange={onYearChange}
          disableFuture
          disabled={isSubmitting}
          className="has-label"
          renderInput={(params) => {
            const inputProps = Object.assign({}, params.inputProps, { placeholder: 'Add year' })
            const customParams = Object.assign({}, params, { inputProps })
            return <TextFieldWithDarkMode isDarkBackdrop={true} theme={theme} {...customParams} />
          }}
        />

        <Stack direction="row" alignItems="center" justifyContent="flex-end" spacing={2}>
          <Button
            variant="text"
            color="primary"
            size="medium"
            onClick={() => {
              onFinishedEditing()
            }}
            sx={{
              width: '120px',
            }}>
            Cancel
          </Button>
          <Button size="medium" variant="contained" color="primary" onClick={onFormSubmit}>
            Save
          </Button>
        </Stack>
      </Stack>
    </>
  )
}

export default AdminOrganizationSidebar
